import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { ExerciseType, SchoolSubject } from '@chancenwerk/enums/enums.js';
import { DateTime } from 'luxon';
import { LevumiMathCategory, LevumiSubject } from '../../enums/CampusLevumiEnums.js';
import { CampusSubject } from '../../enums/CampusSubject.js';
import { ChancenKurveEventlog } from '../../eventlogs/ChancenKurveEventlog.js';
import { ContentResultEventlog } from '../../eventlogs/ContentResultEventlog.js';
import type { ChancenwerkUser } from '../../master-data-management/users/entities/ChancenwerkUser.js';
import { Student } from '../../master-data-management/users/entities/Student.js';
import { CampusExercise } from '../entities/CampusExercise.js';
import { CampusLesson } from '../entities/CampusLesson.js';
import { CampusSection } from '../entities/CampusSection.js';
import { ChancenkurveConfig } from '../entities/ChancenkurveConfig.js';
import { ChancenkurveLevelOverride } from '../entities/ChancenkurveLevelOverride.js';
import { ChancenkurveSubjectRecommendation } from '../entities/ChancenkurveRecommendation.js';
import { ChancenkurveSubjectConfig } from '../entities/ChancenkurveSubjectConfig.js';
import { CampusLevumiLevel } from '../entities/levumi/CampusLevumiLevel.js';
import { LEVUMI_TEST_DURATION_MINUTES } from '../entities/levumi/LevumiConstants.js';
import { createDefaultGermanConfig, createDefaultMathSubtractionConfig } from './chancenkurveConfigDefaults.js';

const getLastChancenKurveFinish = registerQuery({
  type: () => DateTime,
  operationName: 'getLastChancenKurveFinish',
  resolve: (lessonID: string, hash: string) => {
    // ! We use the hash to avoid caching issues
    return async () => {
      const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        _id: 1,
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      if (!user) {
        throw new Error('User not found');
      }

      const col = await getRawCollection<ChancenKurveEventlog>(ChancenKurveEventlog._collectionName);
      const res = await col.findOne(
        { _class: ChancenKurveEventlog._class, lessonID, userID: user._id },
        { sort: { createdAt: -1 } },
      );

      if (!res) {
        return DateTime.now().minus({ years: 10 });
      }
      return DateTime.fromJSDate(res?.createdAt ?? new Date());
    };
  },
  options: {},
  params: [
    { type: () => String, name: 'lessonID' },
    { type: () => String, name: 'hash' },
  ],
});

const commitLastChanceKurveFinish = registerMutation({
  type: () => DateTime,
  operationName: 'commitLastChanceKurveFinish',
  resolve: (lessonID: string) => {
    return async () => {
      const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        _id: 1,
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      if (!user) {
        throw new Error('User not found');
      }

      const eventlog = new ChancenKurveEventlog({ lessonID, userID: user._id, createdAt: new Date() });
      await eventlog.create();

      return DateTime.fromJSDate(eventlog.createdAt);
    };
  },
  options: {},
  params: [{ type: () => String, name: 'lessonID' }],
});

/**
 * Get the global Chancenkurve configuration
 * Config is created on server startup
 */
const getChancenkurveConfig = registerQuery({
  type: () => ChancenkurveConfig,
  operationName: 'getChancenkurveConfig',
  resolve: () => async (gqlFields: BaseQLSelectionSet<ChancenkurveConfig>) => {
    const col = await getCollection(ChancenkurveConfig._class);
    const config = await col.findOne<ChancenkurveConfig>({ _id: '_global' });

    if (!config) {
      throw new Error('Chancenkurve config not found. Server may not have initialized properly.');
    }

    return config;
  },
});

/**
 * Toggle the Chancenkurve feature on or off globally
 * Updates only the active status of the global config
 */
const toggleActiveStatusChancenkurveConfig = registerMutation({
  type: () => ChancenkurveConfig,
  operationName: 'toggleActiveStatusChancenkurveConfig',
  params: [{ type: () => Boolean, name: 'enabled' }],
  resolve: (enabled: boolean) => async (gqlFields: BaseQLSelectionSet<ChancenkurveConfig>) => {
    const config = await getByID(ChancenkurveConfig, '_global')(gqlFields);

    if (!config) {
      throw new Error('Global Chancenkurve config not found. Server may not have initialized properly.');
    }

    (config as ChancenkurveConfig).enabled = enabled;

    logger.info({ enabled }, 'Toggling Chancenkurve active status');
    await config.save();

    return config;
  },
});

/**
 * Update threshold configurations for German and Math Subtraction subjects
 */
const updateChancenkurveThresholds = registerMutation({
  type: () => ChancenkurveConfig,
  operationName: 'updateChancenkurveThresholds',
  params: [
    { type: () => ChancenkurveSubjectConfig, name: 'germanConfig' },
    { type: () => ChancenkurveSubjectConfig, name: 'mathSubtractionConfig' },
  ],
  resolve:
    (germanConfig: ChancenkurveSubjectConfig, mathSubtractionConfig: ChancenkurveSubjectConfig) =>
    async (gqlFields: BaseQLSelectionSet<ChancenkurveConfig>) => {
      const config = await getByID(ChancenkurveConfig, '_global')(gqlFields);

      if (!config) {
        throw new Error('Global Chancenkurve config not found. Server may not have initialized properly.');
      }

      // Note: TypeScript NarrowedEntity typing doesn't recognize these properties
      // but they exist at runtime when the caller requests them in gqlFields
      (config as ChancenkurveConfig).germanConfig = germanConfig;
      (config as ChancenkurveConfig).mathSubtractionConfig = mathSubtractionConfig;

      logger.info('Updating Chancenkurve threshold configurations');
      await config.save();

      return config;
    },
});

/**
 * Get the math category from the student's latest completed math test result.
 * Returns the levumiMathCategory stored in the result (e.g. Subtraktion, Multiplikation, etc.).
 */
async function getMathCategoryFromCompletedTests(userID: string): Promise<Maybe<LevumiMathCategory>> {
  const lastResult = await getLatestTestResult(userID, ExerciseType.LEVUMIMATH);
  return lastResult?.levumiResultInformation?.levumiMathCategory ?? null;
}

/**
 * Get the latest test result for a student and subject
 */
async function getLatestTestResult(userID: string, exerciseType: ExerciseType): Promise<Maybe<ContentResultEventlog>> {
  const col = await getCollection(ContentResultEventlog._class);
  const results = await col
    .find<ContentResultEventlog>({ _class: ContentResultEventlog._class, userID, type: exerciseType })
    .sort({ createdAt: -1 })
    .limit(1)
    .toArray();

  return results[0];
}

/**
 * Find a lesson ID by Levumi level name and subject
 * Queries for lessons that contain exercises with the specified level
 */
async function findLessonByLevel(
  levelName: string,
  subject: LevumiSubject,
  mathCategory: LevumiMathCategory = LevumiMathCategory.SUB,
): Promise<string | null> {
  // First, find the CampusLevumiLevel entity
  const levumiLevelCol = await getRawCollection<CampusLevumiLevel>(CampusLevumiLevel._collectionName);
  const levumiLevel = await levumiLevelCol.findOne({ level: levelName, subject });

  if (!levumiLevel?._id) {
    logger.warn(`[Chancenkurve] No Levumi level found for ${levelName} ${subject}`);
    return null;
  }

  // Aggregation: start from CHANCENkurve sections, join lessons, then exercises
  const sectionCol = await getRawCollection<CampusSection>(CampusSection._collectionName);
  const exerciseMatch =
    subject === LevumiSubject.MATH
      ? { levumiLevelID: levumiLevel._id, levumiMathCategory: mathCategory }
      : { levumiLevelID: levumiLevel._id };
  const result = await sectionCol
    .aggregate([
      // Start with CHANCENkurve sections only
      { $match: { subjects: CampusSubject.CHANCENKURVE } },
      // Join lessons
      { $lookup: { from: CampusLesson._collectionName, localField: 'lessonIDs', foreignField: '_id', as: 'lessons' } },
      { $unwind: '$lessons' },
      // Join exercises for each lesson
      {
        $lookup: {
          from: CampusExercise._collectionName,
          localField: 'lessons.exerciseIDs',
          foreignField: '_id',
          as: 'exercises',
        },
      },
      // Filter to exercises with matching levumiLevelID
      // Math recommendations should target Subtraktion by default.
      { $match: { exercises: { $elemMatch: exerciseMatch } } },
      // Return just the lesson ID
      { $project: { lessonID: '$lessons._id' } },
      { $limit: 1 },
    ])
    .toArray();

  if (result.length === 0) {
    logger.warn(`[Chancenkurve] No CHANCENkurve lesson found for level ${levelName} ${subject}`);
    return null;
  }

  const lessonID = result[0]?.lessonID as string | undefined;
  if (!lessonID) {
    logger.warn(`[Chancenkurve] Aggregation returned no lessonID for level ${levelName} ${subject}`);
    return null;
  }

  logger.info({ lessonID, levelName, subject, mathCategory }, 'Found CHANCENkurve lesson via aggregation');
  return lessonID;
}

/**
 * Get level override for a student and subject if one exists
 */
async function getLevelOverride(userID: string, subject: LevumiSubject): Promise<Maybe<ChancenkurveLevelOverride>> {
  const col = await getRawCollection<ChancenkurveLevelOverride>(ChancenkurveLevelOverride._collectionName);
  return col.findOne({ userID, subject }) as unknown as Promise<Maybe<ChancenkurveLevelOverride>>;
}

/**
 * Clear level override after student completes test at forced level
 */
export async function clearLevelOverride(userID: string, subject: LevumiSubject): Promise<void> {
  const col = await getRawCollection<ChancenkurveLevelOverride>(ChancenkurveLevelOverride._collectionName);
  await col.deleteOne({ userID, subject });
}

/**
 * Get subject config, falling back to defaults if not set
 */
function getSubjectConfigOrDefault(
  subjectConfig: Maybe<ChancenkurveSubjectConfig>,
  subject: 'german' | 'math_subtraction',
): ChancenkurveSubjectConfig {
  if (!subjectConfig) {
    return subject === 'german' ? createDefaultGermanConfig() : createDefaultMathSubtractionConfig();
  }
  return subjectConfig;
}

/**
 * Calculate speed in items per minute from test result
 */
function calculateSpeed(result: ContentResultEventlog): number {
  const totalItems = result.levumiResultInformation?.overallAnswered || 0;
  return totalItems / LEVUMI_TEST_DURATION_MINUTES;
}

/**
 * Calculate accuracy percentage from test result
 */
function calculateAccuracy(result: ContentResultEventlog): number {
  const correct = result.levumiResultInformation?.correctlyAnswered || 0;
  const total = result.levumiResultInformation?.overallAnswered || 1;
  return (correct / total) * 100;
}

/**
 * Determine the recommended test level based on threshold configuration
 */
function determineRecommendedLevel(
  subject: LevumiSubject,
  lastResult: Maybe<ContentResultEventlog>,
  subjectConfig: ChancenkurveSubjectConfig,
): { level: string; reason: string } {
  // If no previous test, start at entry level
  if (!lastResult || !lastResult.levumiResultInformation?.levumiLevel) {
    const entryLevelConfig = subjectConfig.levels?.find(l => l.levelId === subjectConfig.entryLevel);
    const entryLevelName =
      entryLevelConfig?.name ?? subjectConfig.levels?.[0]?.name ?? subjectConfig.entryLevel ?? 'Unbekannt';
    return { level: entryLevelName, reason: `Kein vorheriger Test gefunden - Start mit ${entryLevelName}` };
  }

  const currentLevelName = lastResult.levumiResultInformation.levumiLevel.level;
  const levelConfig = subjectConfig.levels?.find(l => l.name === currentLevelName);

  if (!levelConfig) {
    logger.warn({ currentLevelName, subject }, 'Unknown level in test result, using entry level');
    const entryLevelConfig = subjectConfig.levels?.find(l => l.levelId === subjectConfig.entryLevel);
    const entryLevelName =
      entryLevelConfig?.name ?? subjectConfig.levels?.[0]?.name ?? subjectConfig.entryLevel ?? 'Unbekannt';
    return { level: entryLevelName, reason: `Unbekannter Level ${currentLevelName} - Start mit ${entryLevelName}` };
  }

  const accuracy = calculateAccuracy(lastResult);
  const speed = calculateSpeed(lastResult);

  // Check for far downgrade first (e.g., Station 4 -> Station 2)
  if (levelConfig.moveDownFar) {
    const { targetLevel, maxAccuracyPercent, maxSpeed } = levelConfig.moveDownFar;
    const accuracyFails =
      maxAccuracyPercent !== undefined && maxAccuracyPercent !== null && accuracy <= maxAccuracyPercent;
    const speedFails = maxSpeed !== undefined && maxSpeed !== null && speed <= maxSpeed;

    if (accuracyFails || speedFails) {
      const targetLevelConfig = subjectConfig.levels?.find(l => l.levelId === targetLevel);
      const levelName = targetLevelConfig?.name ?? targetLevel ?? currentLevelName;
      return {
        level: levelName,
        reason: `Runterstufung auf ${targetLevelConfig?.name || targetLevel}: Genauigkeit ${accuracy.toFixed(
          0,
        )}%, Geschwindigkeit ${speed.toFixed(1)} Items/min`,
      };
    }
  }

  // Check for regular downgrade
  if (levelConfig.moveDown) {
    const { targetLevel, maxAccuracyPercent, maxSpeed } = levelConfig.moveDown;
    const accuracyFails =
      maxAccuracyPercent !== undefined && maxAccuracyPercent !== null && accuracy <= maxAccuracyPercent;
    const speedFails = maxSpeed !== undefined && maxSpeed !== null && speed <= maxSpeed;

    if (accuracyFails || speedFails) {
      const targetLevelConfig = subjectConfig.levels?.find(l => l.levelId === targetLevel);
      const levelName = targetLevelConfig?.name ?? targetLevel ?? currentLevelName;
      return {
        level: levelName,
        reason: `Runterstufung auf ${targetLevelConfig?.name || targetLevel}: Genauigkeit ${accuracy.toFixed(
          0,
        )}%, Geschwindigkeit ${speed.toFixed(1)} Items/min`,
      };
    }
  }

  // Check for upgrade
  if (levelConfig.moveUp) {
    const { targetLevel, minAccuracyPercent, minSpeed } = levelConfig.moveUp;
    const accuracyPasses =
      minAccuracyPercent === undefined || minAccuracyPercent === null || accuracy >= minAccuracyPercent;
    const speedPasses = minSpeed === undefined || minSpeed === null || speed >= minSpeed;

    if (accuracyPasses && speedPasses) {
      // Check if this is completion (passed highest level)
      if (targetLevel === 'COMPLETED') {
        return {
          level: 'COMPLETED',
          reason: 'Herzlichen Glückwunsch. Du hast die CHANCENkurve erfolgreich abgeschlossen!',
        };
      }

      const targetLevelConfig = subjectConfig.levels?.find(l => l.levelId === targetLevel);
      const levelName = targetLevelConfig?.name ?? targetLevel ?? currentLevelName;
      return {
        level: levelName,
        reason: `Hochstufung auf ${targetLevelConfig?.name || targetLevel}: Genauigkeit ${accuracy.toFixed(
          0,
        )}%, Geschwindigkeit ${speed.toFixed(1)} Items/min`,
      };
    }
  }

  // Stay at current level
  return {
    level: levelConfig.name ?? currentLevelName,
    reason: `Verbleib auf ${levelConfig.name}: Genauigkeit ${accuracy.toFixed(0)}%, Geschwindigkeit ${speed.toFixed(
      1,
    )} Items/min`,
  };
}

interface RecommendationPlanEntry {
  subject: LevumiSubject;
  /** Only relevant for math subjects. Determines which math category (Subtraktion, Multiplikation, etc.) to use. */
  mathCategory?: LevumiMathCategory;
  source: string;
  /** Whether the ranking/leveling system should be used. False means: redo the exact same lesson. */
  useLeveling: boolean;
  /** When useLeveling is false, the lessonID of the test to redo. */
  redoLessonID?: string | null;
}

/**
 * Get recommended Chancenkurve test(s) for a student.
 *
 * Decision tree (applied per subject — Deutsch and Mathe):
 *
 * Is there a manual override?
 * ├── [YES] ── Is there a completed test?
 * │            ├── [YES] ── Adopt Levumi category & override level
 * │            └── [NO] ─── Use subtraction for math & override level
 * │
 * └── [NO] ─── Is there a completed test?
 *              ├── [YES] ── Is the test German and/or Subtraction?
 *              │            ├── [YES] ── Use in ranking system (with leveling)
 *              │            └── [NO] ─── Redo same test (without leveling)
 *              │
 *              └── [NO] ─── Is there a Math and/or German Förderschwerpunkt?
 *                          ├── [YES] ── Use in ranking system (with leveling)
 *                          └── [NO] ─── Assign Subtraction 3 & German N4 (with leveling)
 */
async function determineRecommendationPlan(
  studentID: string,
  foerderbedarfLautEltern: Maybe<SchoolSubject[]>,
): Promise<RecommendationPlanEntry[]> {
  const plan: RecommendationPlanEntry[] = [];

  const foerderbedarf = foerderbedarfLautEltern ?? [];
  const hasGermanFoerderbedarf = foerderbedarf.includes(SchoolSubject.DEUTSCH);
  const hasMathFoerderbedarf = foerderbedarf.includes(SchoolSubject.MATHE);
  const hasAnyFoerderbedarf = hasGermanFoerderbedarf || hasMathFoerderbedarf;

  // --- German ---
  const germanOverride = await getLevelOverride(studentID, LevumiSubject.GERMAN);
  const lastGermanResult = await getLatestTestResult(studentID, ExerciseType.LEVUMIGERMAN);

  if (germanOverride?.forcedLevel) {
    // Override: always use leveling (German has no category branching)
    plan.push({ subject: LevumiSubject.GERMAN, source: 'override', useLeveling: true });
  } else if (lastGermanResult) {
    // Completed German test: always use leveling
    plan.push({ subject: LevumiSubject.GERMAN, source: 'completedTest', useLeveling: true });
  } else if (hasGermanFoerderbedarf || !hasAnyFoerderbedarf) {
    // Förderschwerpunkt or default
    plan.push({
      subject: LevumiSubject.GERMAN,
      source: hasAnyFoerderbedarf ? 'foerderbedarf' : 'default',
      useLeveling: true,
    });
  }

  // --- Math ---
  const mathOverride = await getLevelOverride(studentID, LevumiSubject.MATH);
  const lastMathResult = await getLatestTestResult(studentID, ExerciseType.LEVUMIMATH);

  if (mathOverride?.forcedLevel) {
    // Override exists: determine category from completed test or default to SUB
    const categoryFromTest = await getMathCategoryFromCompletedTests(studentID);
    plan.push({
      subject: LevumiSubject.MATH,
      mathCategory: categoryFromTest ?? LevumiMathCategory.SUB,
      source: 'override',
      useLeveling: true,
    });
  } else if (lastMathResult) {
    const category = (lastMathResult.levumiResultInformation?.levumiMathCategory ??
      LevumiMathCategory.SUB) as LevumiMathCategory;
    const isSubtraktion = category === LevumiMathCategory.SUB;

    if (isSubtraktion) {
      // Subtraktion: use ranking system with leveling
      plan.push({
        subject: LevumiSubject.MATH,
        mathCategory: LevumiMathCategory.SUB,
        source: 'completedTest',
        useLeveling: true,
      });
    } else {
      // Non-Subtraktion (e.g. Multiplikation, Addition): redo the exact same lesson
      plan.push({
        subject: LevumiSubject.MATH,
        mathCategory: category,
        source: 'completedTest',
        useLeveling: false,
        redoLessonID: lastMathResult.lessonID,
      });
    }
  } else if (hasMathFoerderbedarf || !hasAnyFoerderbedarf) {
    // Förderschwerpunkt or default: Subtraktion with leveling
    plan.push({
      subject: LevumiSubject.MATH,
      mathCategory: LevumiMathCategory.SUB,
      source: hasAnyFoerderbedarf ? 'foerderbedarf' : 'default',
      useLeveling: true,
    });
  }

  return plan;
}

const getChancenkurveRecommendation = registerQuery({
  type: () => [ChancenkurveSubjectRecommendation],
  operationName: 'getChancenkurveRecommendation',
  params: [{ type: () => String, name: 'studentID' }],
  resolve: (studentID: string) => async (gqlFields: BaseQLSelectionSet<ChancenkurveSubjectRecommendation>) => {
    const config = await getChancenkurveConfig()(selectionSet(() => ChancenkurveConfig, 4));
    if (!config?.enabled) {
      return [];
    }

    const student = await getByID(Student, studentID)({ _id: 1, funding: { foerderbedarfLautEltern: 1 } });

    if (!student) {
      return [];
    }

    const recommendationPlan = await determineRecommendationPlan(studentID, student.funding?.foerderbedarfLautEltern);
    const recommendations: ChancenkurveSubjectRecommendation[] = [];

    for (const entry of recommendationPlan) {
      const { subject, mathCategory, source, useLeveling, redoLessonID } = entry;
      const exerciseType = subject === LevumiSubject.GERMAN ? ExerciseType.LEVUMIGERMAN : ExerciseType.LEVUMIMATH;

      // --- Redo without leveling: just recommend the exact same lesson again ---
      if (!useLeveling) {
        const level = 'redo';
        const reason = `Wiederholung des zuletzt bearbeiteten Tests (${mathCategory})`;

        logger.info(
          { studentID, exerciseType, subject, mathCategory, level, lessonID: redoLessonID, reason, source },
          'CHANCENKURVE RECOMMENDATION (redo)',
        );

        recommendations.push(
          new ChancenkurveSubjectRecommendation({
            subject,
            status: 'recommended',
            level,
            lessonID: redoLessonID ?? null,
            reason,
          }),
        );
        continue;
      }

      // --- Leveling path: override or ranking system ---
      const override = await getLevelOverride(studentID, subject);
      let level: string;
      let reason: string;
      let status: 'recommended' | 'completed' | 'override';

      if (override?.forcedLevel) {
        level = override.forcedLevel;
        reason = `Manuell gesetzt von Lehrkraft`;
        status = 'override';
        logger.info({ studentID, subject, level, reason, mathCategory }, 'CHANCENKURVE: Using manual override');
      } else {
        const lastResult = await getLatestTestResult(studentID, exerciseType);

        const subjectConfig =
          subject === LevumiSubject.GERMAN
            ? getSubjectConfigOrDefault(config.germanConfig, 'german')
            : getSubjectConfigOrDefault(config.mathSubtractionConfig, 'math_subtraction');

        const recommendation = determineRecommendedLevel(subject, lastResult, subjectConfig);
        level = recommendation.level;
        reason = recommendation.reason;
        status = level === 'COMPLETED' ? 'completed' : 'recommended';
      }

      const lessonID = level === 'COMPLETED' ? null : await findLessonByLevel(level, subject, mathCategory);

      logger.info(
        { studentID, exerciseType, subject, mathCategory, level, lessonID, reason, status, source },
        'CHANCENKURVE RECOMMENDATION',
      );

      recommendations.push(new ChancenkurveSubjectRecommendation({ subject, status, level, lessonID, reason }));
    }

    return recommendations;
  },
});

/**
 * Get recommended training material for a student
 * Based on the student's current level and the recommended level
 * Returns an array of section IDs
 */
const getChancenkurveMaterialRecommendation = registerQuery({
  type: () => [String], // the section IDs
  operationName: 'getChancenkurveMaterialRecommendation',
  params: [{ type: () => String, name: 'studentID' }],
  resolve: (studentID: string) => async () => {
    const config = await getChancenkurveConfig()(selectionSet(() => ChancenkurveConfig, 4));
    if (!config) {
      return [];
    }

    const student = await getByID(Student, studentID)({ _id: 1, funding: { foerderbedarfLautEltern: 1 } });

    if (!student) {
      return [];
    }

    const recommendationPlan = await determineRecommendationPlan(studentID, student.funding?.foerderbedarfLautEltern);

    const levelTargets: Array<{ subject: LevumiSubject; level: string }> = [];
    for (const { subject } of recommendationPlan) {
      const exerciseType = subject === LevumiSubject.GERMAN ? ExerciseType.LEVUMIGERMAN : ExerciseType.LEVUMIMATH;
      const override = await getLevelOverride(studentID, subject);
      let level: string;

      if (override?.forcedLevel) {
        level = override.forcedLevel;
      } else {
        const lastResult = await getLatestTestResult(studentID, exerciseType);
        const subjectConfig =
          subject === LevumiSubject.GERMAN
            ? getSubjectConfigOrDefault(config.germanConfig, 'german')
            : getSubjectConfigOrDefault(config.mathSubtractionConfig, 'math_subtraction');
        const recommendation = determineRecommendedLevel(subject, lastResult, subjectConfig);
        level = recommendation.level;
      }

      if (level !== 'COMPLETED') {
        levelTargets.push({ subject, level });
      }
    }

    if (levelTargets.length === 0) {
      return [];
    }

    const germanSectionByLevel: Record<string, string> = {
      N2: '64d0b9ff1df6d5cf7f6359c9',
      N4: '64d0a86d1df6d5cf7f635957',
      N6: '62d9422a8e874dac940d3f36',
    };
    const mathSectionID = '63c7aa940b00e0f051ad4d28';

    const sectionIDs = new Set<string>();
    for (const target of levelTargets) {
      if (target.subject === LevumiSubject.GERMAN) {
        const sectionID = germanSectionByLevel[target.level];
        if (sectionID) {
          sectionIDs.add(sectionID);
        }
        continue;
      }

      if (target.subject === LevumiSubject.MATH) {
        sectionIDs.add(mathSectionID);
      }
    }

    return [...sectionIDs];
  },
});

/**
 * Set a manual level override for a student
 * Teachers can use this to force a specific level for the next test
 */
const setChancenkurveLevelOverride = registerMutation({
  type: () => ChancenkurveLevelOverride,
  operationName: 'setChancenkurveLevelOverride',
  params: [
    { type: () => String, name: 'studentID' },
    { type: () => String, name: 'subject' },
    { type: () => String, name: 'forcedLevel' },
    { type: () => String, name: 'reason' },
  ],
  resolve:
    (studentID: string, subject: LevumiSubject, forcedLevel: string, reason?: string) =>
    async (gqlFields: BaseQLSelectionSet<ChancenkurveLevelOverride>) => {
      const currentUser = await CurrentUserInfo.getMyself<ChancenwerkUser>()({ _id: 1 });
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const col = await getRawCollection<ChancenkurveLevelOverride>(ChancenkurveLevelOverride._collectionName);

      // Upsert: update existing or create new
      await col.updateOne(
        { userID: studentID, subject },
        {
          $set: {
            _class: ChancenkurveLevelOverride._class,
            userID: studentID,
            subject,
            forcedLevel,
            setBy: currentUser._id,
            setAt: new Date(),
            reason: reason || undefined,
          },
          $setOnInsert: { _id: A.getGloballyUniqueID() },
        },
        { upsert: true },
      );

      logger.info({ studentID, subject, forcedLevel, setBy: currentUser._id }, 'Level override set');

      const override = await col.findOne({ userID: studentID, subject });
      return override;
    },
});

/**
 * Clear a manual level override for a student
 * Called after student completes test at forced level, or manually by teacher
 */
const clearChancenkurveLevelOverride = registerMutation({
  type: () => Boolean,
  operationName: 'clearChancenkurveLevelOverride',
  params: [
    { type: () => String, name: 'studentID' },
    { type: () => String, name: 'subject' },
  ],
  resolve: (studentID: string, subject: LevumiSubject) => async () => {
    await clearLevelOverride(studentID, subject);
    logger.info({ studentID, subject }, 'Level override cleared');
    return true;
  },
});

/**
 * Get current level override for a student and subject
 */
const getChancenkurveLevelOverride = registerQuery({
  type: () => ChancenkurveLevelOverride,
  operationName: 'getChancenkurveLevelOverride',
  params: [
    { type: () => String, name: 'studentID' },
    { type: () => String, name: 'subject' },
  ],
  resolve:
    (studentID: string, subject: LevumiSubject) => async (gqlFields: BaseQLSelectionSet<ChancenkurveLevelOverride>) => {
      return getLevelOverride(studentID, subject);
    },
});

const subscribeChancenkurveLevelOverridesByUser = registerSubscription({
  type: () => [ChancenkurveLevelOverride],
  operationName: 'subscribeChancenkurveLevelOverridesByUser',
  params: [{ type: () => String, name: 'userID' }],
  resolve: (userID: string) => (gqlFields: BaseQLSelectionSet<ChancenkurveLevelOverride>) => {
    const selector = { _class: ChancenkurveLevelOverride._class, userID };
    return watchQuery(ChancenkurveLevelOverride._collectionName, selector);
  },
});

export namespace chancenKurveAPI {
  export const lastFinish = getLastChancenKurveFinish;
  export const commitFinish = commitLastChanceKurveFinish;
  export const getConfig = getChancenkurveConfig;
  export const updateConfig = toggleActiveStatusChancenkurveConfig;
  export const updateThresholds = updateChancenkurveThresholds;
  export const getRecommendation = getChancenkurveRecommendation;
  export const setLevelOverride = setChancenkurveLevelOverride;
  export const clearLevelOverride = clearChancenkurveLevelOverride;
  export const getLevelOverride = getChancenkurveLevelOverride;
  export const subscribeLevelOverrides = subscribeChancenkurveLevelOverridesByUser;
  export const getMaterialRecommendation = getChancenkurveMaterialRecommendation;
}
